import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserProfile } from '@clerk/react';

const G = '#004D35';
const DARK = '#0a1a0f';
const BG = '#f5f3ef';
const BORDER = '#e8e5e0';
const RED = '#c0392b';
const RED_BG = '#fdf2f2';
const AMBER = '#b45309';
const AMBER_BG = '#fffbeb';
const AMBER_BORDER = '#fcd34d';

function LogoFull() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 32, height: 32, background: G, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
          <path d="M10 11 L20 28 L30 11" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="20" cy="28" r="2.5" fill="#a8f0c6" />
        </svg>
      </div>
      <span style={{ fontSize: 17, fontWeight: 700, color: G, fontFamily: 'Georgia, serif' }}>Viysr</span>
    </div>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 16, padding: '28px 32px', marginBottom: 20 }}>
      {(title || subtitle) && (
        <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${BORDER}` }}>
          {title && <h2 style={{ fontSize: 17, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 4px' }}>{title}</h2>}
          {subtitle && <p style={{ fontSize: 13, color: '#aaa', fontFamily: 'sans-serif', margin: 0 }}>{subtitle}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

function Toggle({ on, onChange, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!on)}
      aria-checked={on}
      role="switch"
      disabled={disabled}
      style={{
        width: 44, height: 24, borderRadius: 999,
        background: on ? G : '#ddd',
        border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: on ? 22 : 3, width: 18, height: 18,
        borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </button>
  );
}

function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: DARK, color: '#fff', padding: '12px 28px', borderRadius: 999, fontSize: 14, fontFamily: 'sans-serif', zIndex: 600, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', whiteSpace: 'nowrap' }}>
      {msg}
    </div>
  );
}

export default function AdvisorSettings() {
  const { getAuthHeader, signOut } = useAuth();
  const navigate = useNavigate();

  const [advisor, setAdvisor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  // Visibility toggle state
  const [isVisible, setIsVisible] = useState(true);
  const [visibilityLoading, setVisibilityLoading] = useState(false);

  // Email preferences state
  const [lifecycleEmails, setLifecycleEmails] = useState(true);
  const [prefsLoading, setPrefsLoading] = useState(false);

  // Delete account state
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  useEffect(() => {
    const load = async () => {
      try {
        const headers = await getAuthHeader();
        const res = await fetch('/api/advisor/me', { headers });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        setAdvisor(data);
        setIsVisible(data.is_visible !== false);
        setLifecycleEmails(data.email_preferences?.lifecycle !== false);
      } catch (err) {
        setError('Failed to load advisor data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [getAuthHeader]);

  const handleVisibilityToggle = async (newVal) => {
    setVisibilityLoading(true);
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/advisor/me/visibility', {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_visible: newVal }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setIsVisible(data.is_visible);
      showToast(data.is_visible ? 'Profile is now visible' : 'Profile is now hidden');
    } catch (err) {
      showToast('Failed to update visibility');
    } finally {
      setVisibilityLoading(false);
    }
  };

  const handleLifecycleToggle = async (newVal) => {
    setPrefsLoading(true);
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/advisor/me/email-preferences', {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lifecycle: newVal }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setLifecycleEmails(data.email_preferences?.lifecycle !== false);
      showToast(newVal ? 'Follow-up reminders enabled' : 'Follow-up reminders disabled');
    } catch (err) {
      showToast('Failed to update email preferences');
    } finally {
      setPrefsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      setError('Type DELETE (all caps) to confirm.');
      return;
    }
    setDeleting(true);
    setError('');
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/advisor/me/delete-account', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmation: 'DELETE' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await signOut();
      navigate('/');
    } catch (err) {
      setError(err.message || 'Deletion failed. Please contact support.');
      setDeleting(false);
    }
  };

  const handleSignOut = async () => { await signOut(); navigate('/'); };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 14, color: '#aaa', fontFamily: 'sans-serif' }}>Loading…</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: BG, fontFamily: 'sans-serif' }}>
      <Toast msg={toast} />

      <nav style={{ background: '#fff', padding: '0 40px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/advisor/dashboard" style={{ textDecoration: 'none' }}><LogoFull /></Link>
          <span style={{ fontSize: 13, color: '#ccc' }}>→</span>
          <Link to="/advisor/dashboard" style={{ fontSize: 13, color: '#aaa', textDecoration: 'none' }}>Dashboard</Link>
          <span style={{ fontSize: 13, color: '#ccc' }}>→</span>
          <span style={{ fontSize: 13, color: '#444', fontWeight: 600 }}>Settings</span>
        </div>
        <button onClick={handleSignOut} style={{ background: 'none', border: 'none', fontSize: 13, color: '#bbb', cursor: 'pointer' }}>Sign out</button>
      </nav>

      <div style={{ maxWidth: 660, margin: '40px auto', padding: '0 24px' }}>

        {error && (
          <div style={{ background: RED_BG, border: `1.5px solid #f5c6c2`, borderRadius: 12, padding: '14px 18px', marginBottom: 20, fontSize: 14, color: RED }}>
            {error}
          </div>
        )}

        {/* ── PROFILE VISIBILITY ── */}
        <SectionCard title="Profile Visibility" subtitle="Control whether your profile appears in search and browse.">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: DARK, marginBottom: 4 }}>
                {isVisible ? '✓ Profile is visible' : '○ Profile is hidden'}
              </div>
              <div style={{ fontSize: 13, color: '#888', lineHeight: 1.5, maxWidth: 380 }}>
                {isVisible
                  ? 'Your profile appears in search results and the browse page. Turning this off hides you without deleting any data.'
                  : 'Your profile is hidden from search and browse. Visitors to your direct URL see a "not currently available" message. Toggling back on restores full visibility immediately.'}
              </div>
            </div>
            <Toggle on={isVisible} onChange={handleVisibilityToggle} disabled={visibilityLoading} />
          </div>
          {!isVisible && (
            <div style={{ marginTop: 16, background: AMBER_BG, border: `1.5px solid ${AMBER_BORDER}`, borderRadius: 10, padding: '12px 16px', fontSize: 13, color: AMBER }}>
              Your profile is hidden. No data has been deleted — toggle on to restore visibility instantly.
            </div>
          )}
        </SectionCard>

        {/* ── EMAIL PREFERENCES ── */}
        <SectionCard title="Email Preferences" subtitle="Control optional reminder emails. Booking and billing emails are always sent.">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: DARK, marginBottom: 2 }}>Follow-up reminders for unresponded leads</div>
              <div style={{ fontSize: 13, color: '#aaa', lineHeight: 1.5, maxWidth: 380 }}>
                We send a reminder nudge after 48 hours when a business owner reaches out and you haven't responded yet.
                Booking confirmations and billing receipts are always sent regardless of this setting.
              </div>
            </div>
            <Toggle on={lifecycleEmails} onChange={handleLifecycleToggle} disabled={prefsLoading} />
          </div>
        </SectionCard>

        {/* ── CLERK ACCOUNT MANAGEMENT ── */}
        <SectionCard title="Account" subtitle="Manage your email address and password through your Clerk account.">
          <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${BORDER}` }}>
            <UserProfile
              appearance={{
                elements: {
                  rootBox: { width: '100%' },
                  card: { boxShadow: 'none', border: 'none', borderRadius: 0 },
                },
              }}
            />
          </div>
        </SectionCard>

        {/* ── DELETE ACCOUNT ── */}
        <SectionCard title="Delete account" subtitle="Permanently remove your profile and cancel your subscription.">
          {!showDeleteSection ? (
            <button
              onClick={() => setShowDeleteSection(true)}
              style={{ background: RED_BG, color: RED, border: `1.5px solid #f5c6c2`, padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
            >
              Delete my account
            </button>
          ) : (
            <div>
              <div style={{ background: RED_BG, border: `1.5px solid #f5c6c2`, borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: RED, marginBottom: 6 }}>This action is permanent and cannot be undone.</div>
                <ul style={{ margin: '0 0 0 16px', padding: 0, fontSize: 13, color: '#666', lineHeight: 1.8 }}>
                  <li>Your Stripe subscription will be cancelled immediately</li>
                  <li>Your advisor profile will be soft-deleted (hidden from all searches and browse)</li>
                  <li>Your profile URL will return a "not available" page</li>
                  <li>Your Viysr account will be permanently removed</li>
                </ul>
              </div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: DARK, marginBottom: 6 }}>
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${deleteConfirm === 'DELETE' ? RED : BORDER}`, borderRadius: 10, fontSize: 14, color: DARK, boxSizing: 'border-box', outline: 'none', marginBottom: 16 }}
              />
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteConfirm !== 'DELETE'}
                  style={{ background: deleteConfirm === 'DELETE' ? RED : '#ddd', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: deleteConfirm === 'DELETE' && !deleting ? 'pointer' : 'not-allowed' }}
                >
                  {deleting ? 'Deleting…' : 'Permanently delete account'}
                </button>
                <button
                  onClick={() => { setShowDeleteSection(false); setDeleteConfirm(''); setError(''); }}
                  style={{ background: '#fff', color: '#666', border: `1.5px solid ${BORDER}`, padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </SectionCard>

      </div>
    </div>
  );
}
